/**
 * Poller module
 * @module @moreonion/socialproof/poller
 */

import {
  distinctUntilKeyChanged,
  Observable,
  retry,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil
} from 'rxjs'

/**
 * A poller that can be used to poll data once or at a scheduled interval.
 */
export default class Poller {
  /**
  * Default settings
  * @type {object}
  */
  static defaults = {
    prop: null,
    interval: 10000,
    multiplier: 2,
    retry: 2,
  }

  // Private observable for scheduled polling
  #scheduledPolling$ = null

  // Private observable for stopping the scheduled polling
  #stop$ = new Subject()

  /**
  * Create a poller.
  *
  * @param {string} url - The URL to poll
  * @param {object} [options] - Options for the poller
  * @param {string|null} [options.prop=null] - Only update subscribers when this property has changed
  * @param {number} [options.interval=10000] - Poll interval for subscriptions in milliseconds
  * @param {number} [options.multiplier=2] - Factor for increasing the interval exponentially
  * @param {number} [options.retry=2] - Number of retries before stopping a poll subscription
  */
  constructor (url, options) {
    /**
     * URL to poll
     * @type {string}
     */
    this.url = url
    /**
     * Instance settings
     * @type {object}
     */
    this.settings = {
      ...Poller.defaults,
      ...options,
    }
  }

  /**
   * Fetch current data from the poll URL.
   */
  async poll () {
    const response = await fetch(this.url)
    if (!response.ok) {
      throw new Error(`HTTP status code ${response.status} ${response.statusText}`)
    }
    return response.json()
  }

  /**
   * Start scheduled polling.
   */
  start () {
    // Poll and cancel the previous poll if it hasn’t finished yet.
    const pipeArgs = [switchMap(() => this.poll())]
    // Multicast the polled data to multiple observers.
    // - bufferSize: Resend the last value for new subscribers.
    // - refCount: Stop when the last observer unsubscribes.
    pipeArgs.push(shareReplay({ bufferSize: 1, refCount: true }))
    // Add a stop signal
    pipeArgs.push(takeUntil(this.#stop$))
    // Number of retries on error before throwing the error.
    pipeArgs.push(retry(this.settings.retry))
    if (this.settings.prop) {
      // Only emit when a specified value has changed in the polled data.
      pipeArgs.push(distinctUntilKeyChanged(this.settings.prop))
    }
    // Schedule emissions.
    const schedule = (observer) => {
      let count = 0
      const next = () => {
        const interval = Math.pow(this.settings.multiplier, count) * this.settings.interval
        if (!observer.closed) {
          observer.next(count++)
          setTimeout(next, interval)
        }
      }
      next()
    }

    this.#scheduledPolling$ = new Observable(schedule).pipe(...pipeArgs)
  }

  /**
   * Stop polling data and unsubscribe all observers.
   */
  stop () {
    this.#stop$.next()
  }

  /**
   * Add a new observer for the polled data.
   *
   * @param {function|object} observer - Callback for each event or object map with callbacks
   * @param {function} [observer.next] - Success callback
   * @param {function} [observer.error] - Error callback
   * @param {number} [limit=Infinity] - Number of events to accept
   *
   * @returns {rxjs.Subscription} The observer’s subscription
   */
  subscribe (observer, limit = Infinity) {
    if (!this.#scheduledPolling$) {
      this.start()
    }
    return this.#scheduledPolling$.pipe(take(limit)).subscribe(observer)
  }
}
